import { useTranslation } from '../../../../../shared/i18n';
import React from 'react';
import { Button } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, CrownFilled, ShoppingCartOutlined, CheckOutlined, SafetyCertificateOutlined, LockOutlined, EyeInvisibleOutlined, SyncOutlined, GlobalOutlined } from '@ant-design/icons';
import { formatMoney } from '../../../../../shared/utils/money';
import { resultState, resultKey } from './searchUtils';

/** 优惠幅度(%)：特价相对原价 */
export const discountPct = (price, list) => {
    const p = Number(price);
    const l = Number(list);
    if (!(l > 0) || !(p > 0) || p >= l) return 0;
    return Math.round((1 - p / l) * 100);
};

/** 把域名拆成「名字 + .后缀」，后缀上色 */
export const DomainName = ({ r, tldUnicode, className = '' }) => {
    const full = r.domain_unicode || r.domain || '';
    const tld = tldUnicode || r.tld_unicode || r.tld || '';
    const cut = tld && full.toLowerCase().endsWith(`.${String(tld).toLowerCase()}`) ? full.length - tld.length - 1 : -1;
    return (
        <span className={`ds-name ${className}`}>
            {cut > 0 ? (
                <>
                    <span>{full.slice(0, cut)}</span>
                    <span className="ds-tld">{full.slice(cut)}</span>
                </>
            ) : full}
        </span>
    );
};

/** 精选后缀卡片（未搜索时） */
export const TldShowcase = ({ items = [], onPick }) => {
    const { t } = useTranslation();
    if (!items.length) return null;
    return (
        <div className="ds-showcase">
            {items.map((item) => {
                const promo = item.promo && Number(item.promo.price) < Number(item.registration_price) ? item.promo : null;
                const off = promo ? discountPct(promo.price, item.registration_price) : 0;
                return (
                    <button type="button" key={item.tld} className="ds-tldcard" onClick={() => onPick(item.tld)}>
                        {off > 0 && <span className="ds-off">-{off}%</span>}
                        <div className="ds-tldcard-name">.{item.tld_unicode || item.tld}</div>
                        <div className="ds-tldcard-price">
                            {formatMoney(promo ? promo.price : item.registration_price, item.currency)}
                            {promo && <s>{formatMoney(item.registration_price, item.currency)}</s>}
                        </div>
                        {item.renewal_price != null && (
                            <div className="ds-tldcard-sub">{t('Renew for {{price}}/year', { price: formatMoney(item.renewal_price, item.currency) })}</div>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

const PERKS = [
    { icon: <GlobalOutlined />, title: '免费 DNS 解析' },
    { icon: <LockOutlined />, title: '域名锁防劫持' },
    { icon: <EyeInvisibleOutlined />, title: '隐私保护', hint: '支持的后缀' },
    { icon: <SyncOutlined />, title: '到期自动续费' },
    { icon: <SafetyCertificateOutlined />, title: '一键接入 CDN / SSL' },
];

/** 每个域名都包含 */
export const Perks = () => {
    const { t } = useTranslation();
    return (
        <div className="ds-perks">
            {PERKS.map((p) => (
                <div key={p.title} className="ds-perk">
                    <span className="ds-perk-icon">{p.icon}</span>
                    <span>{t(p.title)}{p.hint && <small>{t(p.hint)}</small>}</span>
                </div>
            ))}
        </div>
    );
};

/** 价格块：首年(特价划线) + 之后续费 */
export const PriceBlock = ({ r, big = false }) => {
    const { t } = useTranslation();
    const p = r.pricing || {};
    if (p.register == null) return null;
    const list = p.list_price != null && Number(p.list_price) > Number(p.register) + 0.001 ? Number(p.list_price) : null;
    return (
        <div className={`ds-priceblock ${big ? 'is-big' : ''}`}>
            <div className="ds-pb-first">
                {list != null && <s>{formatMoney(list, p.currency)}</s>}
                <b className={list != null ? 'is-promo' : ''}>{formatMoney(p.register, p.currency)}</b>
                <span>{t('First year')}</span>
            </div>
            {p.renew != null && (
                <div className={`ds-pb-then ${Number(p.renew) > Number(p.register) + 0.001 ? 'is-higher' : ''}`}>
                    {t('Then {{price}}/year', { price: formatMoney(p.renew, p.currency) })}
                </div>
            )}
        </div>
    );
};

/** 精确匹配 / 首选结果大卡片 */
export const ExactCard = ({ r, tldUnicode, inCart, adding, onAdd, onTransfer, onCheckout, pending }) => {
    const { t } = useTranslation();
    if (pending) {
        return (
            <div className="ds-exact is-pending">
                <div className="ds-exact-main">
                    <div className="ds-exact-status"><SyncOutlined spin /> {t('Querying…')}</div>
                    <div className="ds-exact-domain">{pending}</div>
                </div>
            </div>
        );
    }
    if (!r) return null;
    const state = resultState(r);
    const canBuy = state === 'available' || state === 'premium';
    const added = inCart?.has(resultKey(r));
    const statusEl = {
        available: <><CheckCircleFilled className="ok" /> {t('Good news! Available to register')}</>,
        premium: <><CrownFilled className="gold" /> {t('Premium domain available for registration')}</>,
        taken: <><CloseCircleFilled className="no" /> {t('Already registered')}</>,
    }[state] || <><CloseCircleFilled className="no" /> {t('Currently unavailable for registration')}</>;
    const off = r.pricing?.promo ? discountPct(r.pricing.register, r.pricing.list_price) : 0;

    return (
        <div className={`ds-exact ${canBuy ? 'is-ok' : 'is-no'}`}>
            <div className="ds-exact-main">
                <div className="ds-exact-status">{statusEl}{off > 0 && <span className="ds-off inline">{t('Special offer -{{off}}%', { off })}</span>}</div>
                <div className="ds-exact-domain"><DomainName r={r} tldUnicode={tldUnicode} /></div>
            </div>
            <div className="ds-exact-side">
                {canBuy && <PriceBlock r={r} big />}
                {canBuy && (added ? (
                    <Button size="large" icon={<CheckOutlined />} onClick={onCheckout}>{t('Added · Checkout')}</Button>
                ) : (
                    <Button size="large" type="primary" icon={<ShoppingCartOutlined />} loading={adding} onClick={() => onAdd(r)}>
                        {t('Add to cart')}
                    </Button>
                ))}
                {state === 'taken' && onTransfer && (
                    <Button size="large" onClick={() => onTransfer(r)}>{t('It\'s mine, transfer in')}</Button>
                )}
            </div>
        </div>
    );
};

/** 组合注册：同名多个热门后缀一起加入（保护品牌） */
export const BundleCard = ({ rows = [], label, tldOf, onAddAll, busy, allInCart }) => {
    const { t } = useTranslation();
    if (rows.length < 2) return null;
    const cur = rows[0]?.pricing?.currency;
    const total = rows.reduce((s, r) => s + Number(r.pricing?.register || 0), 0);
    return (
        <div className="ds-bundle">
            <div className="ds-bundle-main">
                <div className="ds-bundle-title">{t('Register together to protect your brand')}</div>
                <div className="ds-bundle-names">
                    <span className="ds-bundle-label">{label}</span>
                    {rows.map((r, i) => (
                        <React.Fragment key={resultKey(r)}>
                            {i > 0 && <span className="ds-plus">+</span>}
                            <span className="ds-bundle-tld">.{tldOf(r)}</span>
                        </React.Fragment>
                    ))}
                </div>
            </div>
            <div className="ds-exact-side">
                <div className="ds-priceblock is-big">
                    <div className="ds-pb-first"><b>{formatMoney(total, cur)}</b><span>{t('First-year total')}</span></div>
                </div>
                <Button size="large" type={allInCart ? 'default' : 'primary'} icon={allInCart ? <CheckOutlined /> : <ShoppingCartOutlined />} loading={busy} disabled={allInCart} onClick={onAddAll}>
                    {allInCart ? t('All added') : t('Add all')}
                </Button>
            </div>
        </div>
    );
};
